import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Badge, Field, TextField, SelectField, Loading } from '../../components/ui.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { MediaThumb } from '../../components/MediaThumb.tsx';
import { MediaPickerModal } from '../../components/MediaPickerModal.tsx';
import { OverlayForm } from './OverlayForm.tsx';
import { usePermissions } from '../../auth/session.tsx';
import { useBrand, useUpdateBrand } from '../../api/brand.ts';
import { useOverlays, useDeleteOverlay, type Overlay } from '../../api/overlays.ts';
import { useOrg, useUpdateOrg, orgDefaultOverlayId } from '../../api/org.ts';
import { useMedia } from '../../api/media.ts';
import { ApiError } from '../../api/client.ts';

// System-font choices (no webfont loading — signage renders with what the OS has).
const FONTS: Array<{ label: string; value: string }> = [
  { label: 'System default', value: 'system-ui' },
  { label: 'Inter', value: 'Inter' },
  { label: 'Helvetica / Arial', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
  { label: 'Georgia (serif)', value: 'Georgia, serif' },
  { label: 'Times New Roman (serif)', value: '"Times New Roman", serif' },
  { label: 'Courier (mono)', value: '"Courier New", monospace' },
];

const DEFAULT_COLORS = ['#2f6bff', '#14264a', '#ffffff'];

function padColors(colors: string[]): [string, string, string] {
  return [colors[0] ?? DEFAULT_COLORS[0]!, colors[1] ?? DEFAULT_COLORS[1]!, colors[2] ?? DEFAULT_COLORS[2]!];
}

function overlaySummary(o: Overlay): string {
  const parts: string[] = [];
  if (o.logo_media_id) parts.push(`Logo (${o.position}, ${o.size_pct}%, ${o.opacity}%)`);
  if (o.ticker_text) parts.push('Ticker');
  return parts.join(' · ') || 'Empty';
}

function BrandKitCard() {
  const perms = usePermissions();
  const brand = useBrand();
  const media = useMedia({ kind: 'image' });
  const update = useUpdateBrand();
  const readOnly = !perms.canManageOrg;

  const [logoId, setLogoId] = useState<string | null>(null);
  const [colors, setColors] = useState<[string, string, string]>(padColors([]));
  const [font, setFont] = useState('system-ui');
  const [tone, setTone] = useState('');
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    if (brand.data) {
      setLogoId(brand.data.logo_media_id);
      setColors(padColors(brand.data.colors));
      setFont(brand.data.font ?? 'system-ui');
      setTone(brand.data.tone ?? '');
    }
  }, [brand.data]);

  if (brand.isLoading || !brand.data) return <div className="card card-pad"><Loading /></div>;

  const logoMedia = (media.data ?? []).find((m) => m.id === logoId);

  function save() {
    update.mutate(
      { logo_media_id: logoId, colors, font: font || null, tone: tone.trim() || null },
      {
        onSuccess: () => toast.success('Brand kit saved'),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
      },
    );
  }

  return (
    <div className="card card-pad">
      <div className="card-title">Brand kit</div>
      <p className="hint mb-4">Your logo, colors, font, and tone — reused across overlays and generated slides.</p>

      <Field label="Logo">
        <div className="ov-logo-row">
          {logoId ? (
            <>
              <MediaThumb
                media={
                  logoMedia ?? {
                    id: logoId, org_id: '', kind: 'image', status: 'ready', filename: null, mime: null,
                    size_bytes: null, width: null, height: null, duration_ms: null, src_url: null, created_at: 0,
                  }
                }
                className="picker-thumb"
              />
              <span className="cell-dim" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {logoMedia?.filename ?? 'Selected image'}
              </span>
              {!readOnly && <Button size="sm" onClick={() => setPicking(true)}>Change</Button>}
              {!readOnly && <Button size="sm" variant="ghost" onClick={() => setLogoId(null)}>Remove</Button>}
            </>
          ) : readOnly ? (
            <span className="text-dim">No logo set</span>
          ) : (
            <Button size="sm" onClick={() => setPicking(true)}>Choose logo…</Button>
          )}
        </div>
      </Field>

      <Field label="Brand colors" hint="Primary, secondary, and accent.">
        <div className="ov-swatches">
          {colors.map((c, i) => (
            <input
              key={i}
              type="color"
              aria-label={`Brand color ${i + 1}`}
              value={c}
              disabled={readOnly}
              onChange={(e) => setColors((prev) => {
                const next = [...prev] as [string, string, string];
                next[i] = e.target.value;
                return next;
              })}
            />
          ))}
        </div>
      </Field>

      <SelectField label="Font" value={font} disabled={readOnly} onChange={(e) => setFont(e.target.value)}>
        {FONTS.map((f) => (
          <option key={f.value} value={f.value}>{f.label}</option>
        ))}
      </SelectField>

      <TextField
        label="Tone of voice"
        hint="One line — how your copy should sound (feeds generated content)."
        value={tone}
        disabled={readOnly}
        onChange={(e) => setTone(e.target.value)}
        placeholder="e.g. Warm, concise, a little playful"
      />

      {!readOnly && (
        <Button variant="primary" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save brand kit'}
        </Button>
      )}

      {picking && (
        <MediaPickerModal
          title="Choose a logo"
          onPick={(id) => { setLogoId(id); setPicking(false); }}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

function OverlaysCard() {
  const perms = usePermissions();
  const overlays = useOverlays();
  const org = useOrg();
  const del = useDeleteOverlay();
  const setDefault = useUpdateOrg();
  const brand = useBrand();
  const readOnly = !perms.canManageContent;

  const [editing, setEditing] = useState<Overlay | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Overlay | null>(null);

  const defaultId = orgDefaultOverlayId(org.data);
  const accent = brand.data?.colors[0] ?? null;

  function toggleDefault(o: Overlay) {
    const next = defaultId === o.id ? null : o.id;
    setDefault.mutate(
      { default_overlay_id: next },
      {
        onSuccess: () => toast.success(next ? `“${o.name}” is now the default` : 'Default overlay cleared'),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
      },
    );
  }

  return (
    <div className="card card-pad">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Overlays</div>
        {!readOnly && <Button size="sm" variant="primary" onClick={() => setCreating(true)}>New overlay</Button>}
      </div>
      <p className="hint mt-2 mb-4">A logo watermark and/or ticker shown above everything. Assign one as the default for all screens, or per-screen from a screen’s detail.</p>

      {overlays.isLoading ? (
        <Loading />
      ) : (overlays.data ?? []).length === 0 ? (
        <p className="text-dim">No overlays yet.{!readOnly && ' Create one to brand your screens.'}</p>
      ) : (
        <div className="ov-list">
          {overlays.data!.map((o) => (
            <div key={o.id} className="ov-list-item">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="row" style={{ gap: 'var(--sp-2)' }}>
                  <span className="cell-strong">{o.name}</span>
                  {defaultId === o.id && <Badge tone="info">Default</Badge>}
                </div>
                <div className="cell-dim" style={{ fontSize: 'var(--fs-xs)' }}>{overlaySummary(o)}</div>
              </div>
              {!readOnly && (
                <div className="row" style={{ gap: 'var(--sp-2)' }}>
                  <Button size="sm" onClick={() => toggleDefault(o)} disabled={setDefault.isPending}>
                    {defaultId === o.id ? 'Clear default' : 'Set default'}
                  </Button>
                  <Button size="sm" onClick={() => setEditing(o)}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => setConfirmDelete(o)}>Delete</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && <OverlayForm accent={accent} onClose={() => setCreating(false)} />}
      {editing && <OverlayForm overlay={editing} accent={accent} onClose={() => setEditing(null)} />}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete overlay?"
          message={`“${confirmDelete.name}” will be removed. Screens using it fall back to the default (or nothing).`}
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            del.mutate(confirmDelete.id, {
              onSuccess: () => { toast.success('Overlay deleted'); setConfirmDelete(null); },
              onError: (e) =>
                toast.error(
                  e instanceof ApiError && e.isConflict
                    ? 'This overlay is in use — clear it from screens/default first.'
                    : e instanceof ApiError ? e.message : 'Delete failed',
                ),
            })
          }
        />
      )}
    </div>
  );
}

/** One Settings section (docs/09 §C2/§D): brand kit + overlays management. */
export function BrandSection() {
  return (
    <div className="grid" style={{ gap: 'var(--sp-4)' }}>
      <BrandKitCard />
      <OverlaysCard />
    </div>
  );
}
